import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaCode,
  FaYoutube,
  FaLink,
  FaList,
  FaGripVertical,
  FaSave,
  FaTimes,
  FaImage,
  FaEye,
  FaClock,
  FaMapSigns,
} from "react-icons/fa";
import axios from "../../utils/axiosConfig";
import toast from "react-hot-toast";

const LearningRoadmapManager = ({ isDarkMode }) => {
  const [domains, setDomains] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("domains");
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainForm, setDomainForm] = useState({
    name: "",
    description: "",
    icon: "FaCode",
    color: "#ff6a00",
    tags: "",
    order: 0,
  });
  const [topicForm, setTopicForm] = useState({
    title: "",
    description: "",
    domainId: "",
    order: 0,
    learningOutcomes: "",
    prerequisites: "",
    tags: "",
    estimatedTime: 30,
    difficulty: "beginner",
    primaryVideo: "",
    resources: [],
  });

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    if (selectedDomain) {
      fetchTopics(selectedDomain);
    }
  }, [selectedDomain]);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/roadmap/admin/domains", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDomains(response.data.domains);
    } catch (error) {
      console.error("Fetch domains error:", error);
      toast.error("Failed to fetch domains");
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async (domainId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/roadmap/admin/topics", {
        params: { domainId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTopics(response.data.topics);
    } catch (error) {
      console.error("Fetch topics error:", error);
      toast.error("Failed to fetch topics");
    }
  };

  const handleSaveDomain = async () => {
    if (!domainForm.name || !domainForm.description) {
      toast.error("Name and description are required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const data = {
        name: domainForm.name,
        description: domainForm.description,
        icon: domainForm.icon || "FaCode",
        color: domainForm.color,
        tags: domainForm.tags
          ? domainForm.tags.split(",").map((t) => t.trim())
          : [],
        order: domainForm.order || 0,
      };

      let response;
      if (editingDomain) {
        response = await axios.put(
          `/api/roadmap/admin/domains/${editingDomain._id}`,
          data,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("Domain updated");
      } else {
        response = await axios.post("/api/roadmap/admin/domains", data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Domain created");
      }
      fetchDomains();
      setShowDomainModal(false);
      resetDomainForm();
    } catch (error) {
      console.error("Save domain error:", error);
      toast.error(error.response?.data?.message || "Failed to save domain");
    }
  };

  const handleDeleteDomain = async (domain) => {
    if (!window.confirm(`Delete "${domain.name}" and all its topics?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/roadmap/admin/domains/${domain._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Domain deleted");
      fetchDomains();
      if (selectedDomain === domain._id) {
        setSelectedDomain(null);
        setTopics([]);
      }
    } catch (error) {
      console.error("Delete domain error:", error);
      toast.error("Failed to delete domain");
    }
  };

  const handleSaveTopic = async () => {
    if (
      !topicForm.title ||
      !topicForm.description ||
      !topicForm.domainId ||
      !topicForm.primaryVideo
    ) {
      toast.error("Title, description, domain, and video URL are required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const data = {
        title: topicForm.title,
        description: topicForm.description,
        domainId: topicForm.domainId,
        order: topicForm.order || 0,
        learningOutcomes: topicForm.learningOutcomes
          ? topicForm.learningOutcomes.split("\n").filter((l) => l.trim())
          : [],
        prerequisites: topicForm.prerequisites
          ? topicForm.prerequisites.split("\n").filter((p) => p.trim())
          : [],
        tags: topicForm.tags
          ? topicForm.tags.split(",").map((t) => t.trim())
          : [],
        estimatedTime: topicForm.estimatedTime || 30,
        difficulty: topicForm.difficulty,
        primaryVideo: topicForm.primaryVideo,
        resources: topicForm.resources || [],
      };

      if (editingTopic) {
        await axios.put(`/api/roadmap/admin/topics/${editingTopic._id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Topic updated");
      } else {
        await axios.post("/api/roadmap/admin/topics", data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Topic created");
      }
      fetchTopics(selectedDomain);
      setShowTopicModal(false);
      resetTopicForm();
    } catch (error) {
      console.error("Save topic error:", error);
      toast.error(error.response?.data?.message || "Failed to save topic");
    }
  };

  const handleDeleteTopic = async (topic) => {
    if (!window.confirm(`Delete "${topic.title}"?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/roadmap/admin/topics/${topic._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Topic deleted");
      fetchTopics(selectedDomain);
    } catch (error) {
      console.error("Delete topic error:", error);
      toast.error("Failed to delete topic");
    }
  };

  const resetDomainForm = () => {
    setDomainForm({
      name: "",
      description: "",
      icon: "FaCode",
      color: "#ff6a00",
      tags: "",
      order: 0,
    });
    setEditingDomain(null);
  };

  const resetTopicForm = () => {
    setTopicForm({
      title: "",
      description: "",
      domainId: selectedDomain || "",
      order: topics.length,
      learningOutcomes: "",
      prerequisites: "",
      tags: "",
      estimatedTime: 30,
      difficulty: "beginner",
      primaryVideo: "",
      resources: [],
    });
    setEditingTopic(null);
  };

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getButtonStyle = (isActive) => ({
    padding: "0.5rem 1rem",
    borderBottom: isActive ? "3px solid #8b5cf6" : "none",
    color: isActive ? "#8b5cf6" : getTextColor(),
    background: "none",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    cursor: "pointer",
  });

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <FaSpinner
          style={{
            animation: "spin 1s linear infinite",
            fontSize: "2rem",
            color: "#8b5cf6",
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1.5rem",
          borderBottom: `2px solid ${getBorderColor()}`,
        }}
      >
        <button
          onClick={() => setActiveTab("domains")}
          style={getButtonStyle(activeTab === "domains")}
        >
          Domains ({domains.length})
        </button>
        <button
          onClick={() => setActiveTab("topics")}
          style={getButtonStyle(activeTab === "topics")}
        >
          Topics ({topics.length})
        </button>
      </div>

      {activeTab === "domains" && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "1rem",
            }}
          >
            <button
              onClick={() => {
                resetDomainForm();
                setShowDomainModal(true);
              }}
              style={{
                padding: "0.5rem 1rem",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaPlus /> Add Domain
            </button>
          </div>

          <div style={{ display: "grid", gap: "1rem" }}>
            {domains.map((domain) => (
              <motion.div
                key={domain._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  background: getCardBackground(),
                  borderRadius: "16px",
                  padding: "1rem",
                  border: `1px solid ${getBorderColor()}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "1rem",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setSelectedDomain(domain._id);
                  setActiveTab("topics");
                }}
              >
                <div>
                  <h4 style={{ color: getTextColor(), marginBottom: "0.3rem" }}>
                    {domain.name}
                  </h4>
                  <p
                    style={{
                      color: getTextSecondary(),
                      fontSize: "0.8rem",
                      maxWidth: "400px",
                    }}
                  >
                    {domain.description?.substring(0, 100)}...
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      marginTop: "0.3rem",
                    }}
                  >
                    <span style={{ fontSize: "0.7rem", color: domain.color }}>
                      🎨 {domain.color}
                    </span>
                    <span
                      style={{ fontSize: "0.7rem", color: getTextSecondary() }}
                    >
                      📚 {domain.totalTopics} topics
                    </span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingDomain(domain);
                      setDomainForm({
                        name: domain.name,
                        description: domain.description,
                        icon: domain.icon,
                        color: domain.color,
                        tags: domain.tags?.join(", ") || "",
                        order: domain.order || 0,
                      });
                      setShowDomainModal(true);
                    }}
                    style={{
                      padding: "0.3rem 0.8rem",
                      background: "#17a2b8",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDomain(domain);
                    }}
                    style={{
                      padding: "0.3rem 0.8rem",
                      background: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "topics" && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <select
              value={selectedDomain || ""}
              onChange={(e) => setSelectedDomain(e.target.value)}
              style={{
                padding: "0.5rem 1rem",
                border: `2px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getCardBackground(),
                color: getTextColor(),
              }}
            >
              <option value="">Select Domain</option>
              {domains.map((domain) => (
                <option key={domain._id} value={domain._id}>
                  {domain.name}
                </option>
              ))}
            </select>
            {selectedDomain && (
              <button
                onClick={() => {
                  resetTopicForm();
                  setTopicForm({ ...topicForm, domainId: selectedDomain });
                  setShowTopicModal(true);
                }}
                style={{
                  padding: "0.5rem 1rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <FaPlus /> Add Topic
              </button>
            )}
          </div>

          {!selectedDomain ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                color: getTextSecondary(),
              }}
            >
              Select a domain to manage topics
            </div>
          ) : topics.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                color: getTextSecondary(),
              }}
            >
              No topics in this domain. Click "Add Topic" to create one.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "1rem" }}>
              {topics.map((topic, idx) => (
                <motion.div
                  key={topic._id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    background: getCardBackground(),
                    borderRadius: "16px",
                    padding: "1rem",
                    border: `1px solid ${getBorderColor()}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          flexWrap: "wrap",
                          marginBottom: "0.3rem",
                        }}
                      >
                        <span style={{ fontSize: "0.7rem", color: "#8b5cf6" }}>
                          #{idx + 1}
                        </span>
                        <h4 style={{ color: getTextColor(), margin: 0 }}>
                          {topic.title}
                        </h4>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            padding: "0.2rem 0.5rem",
                            borderRadius: "4px",
                            background: "#8b5cf620",
                            color: "#8b5cf6",
                          }}
                        >
                          {topic.difficulty}
                        </span>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: getTextSecondary(),
                          }}
                        >
                          <FaClock
                            style={{ marginRight: "0.2rem", display: "inline" }}
                          />
                          {topic.estimatedTime} min
                        </span>
                      </div>
                      <p
                        style={{
                          color: getTextSecondary(),
                          fontSize: "0.8rem",
                          marginBottom: "0.3rem",
                        }}
                      >
                        {topic.description?.substring(0, 120)}...
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          flexWrap: "wrap",
                          fontSize: "0.7rem",
                          color: getTextSecondary(),
                        }}
                      >
                        <span>
                          🎬 {topic.primaryVideo?.substring(0, 40)}...
                        </span>
                        <span>📚 {topic.resources?.length || 0} resources</span>
                        <span>🏷️ {topic.tags?.join(", ") || "No tags"}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        onClick={() => {
                          setEditingTopic(topic);
                          setTopicForm({
                            title: topic.title,
                            description: topic.description,
                            domainId: topic.domainId?._id || topic.domainId,
                            order: topic.order,
                            learningOutcomes:
                              topic.learningOutcomes?.join("\n") || "",
                            prerequisites:
                              topic.prerequisites?.join("\n") || "",
                            tags: topic.tags?.join(", ") || "",
                            estimatedTime: topic.estimatedTime,
                            difficulty: topic.difficulty,
                            primaryVideo: topic.primaryVideo,
                            resources: topic.resources || [],
                          });
                          setShowTopicModal(true);
                        }}
                        style={{
                          padding: "0.3rem 0.8rem",
                          background: "#17a2b8",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTopic(topic)}
                        style={{
                          padding: "0.3rem 0.8rem",
                          background: "#dc3545",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Domain Modal */}
      <AnimatePresence>
        {showDomainModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => setShowDomainModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                background: getCardBackground(),
                borderRadius: "20px",
                maxWidth: "500px",
                width: "90%",
                padding: "1.5rem",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ color: getTextColor(), marginBottom: "1rem" }}>
                {editingDomain ? "Edit Domain" : "Add Domain"}
              </h3>
              <input
                type="text"
                placeholder="Domain Name *"
                value={domainForm.name}
                onChange={(e) =>
                  setDomainForm({ ...domainForm, name: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginBottom: "1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "8px",
                  background: getCardBackground(),
                  color: getTextColor(),
                }}
              />
              <textarea
                placeholder="Description *"
                value={domainForm.description}
                onChange={(e) =>
                  setDomainForm({ ...domainForm, description: e.target.value })
                }
                rows="3"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginBottom: "1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "8px",
                  background: getCardBackground(),
                  color: getTextColor(),
                }}
              />
              <input
                type="color"
                value={domainForm.color}
                onChange={(e) =>
                  setDomainForm({ ...domainForm, color: e.target.value })
                }
                style={{
                  width: "100%",
                  marginBottom: "1rem",
                  padding: "0.3rem",
                  borderRadius: "8px",
                }}
              />
              <input
                type="text"
                placeholder="Tags (comma separated)"
                value={domainForm.tags}
                onChange={(e) =>
                  setDomainForm({ ...domainForm, tags: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginBottom: "1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "8px",
                  background: getCardBackground(),
                  color: getTextColor(),
                }}
              />
              <div style={{ display: "flex", gap: "1rem" }}>
                <button
                  onClick={() => setShowDomainModal(false)}
                  style={{
                    flex: 1,
                    padding: "0.8rem",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDomain}
                  style={{
                    flex: 1,
                    padding: "0.8rem",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Topic Modal */}
      <AnimatePresence>
        {showTopicModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              overflow: "auto",
            }}
            onClick={() => setShowTopicModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                background: getCardBackground(),
                borderRadius: "20px",
                maxWidth: "600px",
                width: "90%",
                padding: "1.5rem",
                maxHeight: "90vh",
                overflow: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ color: getTextColor(), marginBottom: "1rem" }}>
                {editingTopic ? "Edit Topic" : "Add Topic"}
              </h3>
              <input
                type="text"
                placeholder="Title *"
                value={topicForm.title}
                onChange={(e) =>
                  setTopicForm({ ...topicForm, title: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginBottom: "1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "8px",
                  background: getCardBackground(),
                  color: getTextColor(),
                }}
              />
              <textarea
                placeholder="Description *"
                value={topicForm.description}
                onChange={(e) =>
                  setTopicForm({ ...topicForm, description: e.target.value })
                }
                rows="3"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginBottom: "1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "8px",
                  background: getCardBackground(),
                  color: getTextColor(),
                }}
              />
              <input
                type="text"
                placeholder="Primary Video URL (YouTube) *"
                value={topicForm.primaryVideo}
                onChange={(e) =>
                  setTopicForm({ ...topicForm, primaryVideo: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginBottom: "1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "8px",
                  background: getCardBackground(),
                  color: getTextColor(),
                }}
              />
              <div
                style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}
              >
                <select
                  value={topicForm.difficulty}
                  onChange={(e) =>
                    setTopicForm({ ...topicForm, difficulty: e.target.value })
                  }
                  style={{
                    flex: 1,
                    padding: "0.8rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "8px",
                    background: getCardBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
                <input
                  type="number"
                  placeholder="Est. Time (min)"
                  value={topicForm.estimatedTime}
                  onChange={(e) =>
                    setTopicForm({
                      ...topicForm,
                      estimatedTime: parseInt(e.target.value),
                    })
                  }
                  style={{
                    flex: 1,
                    padding: "0.8rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "8px",
                    background: getCardBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>
              <textarea
                placeholder="Learning Outcomes (one per line)"
                value={topicForm.learningOutcomes}
                onChange={(e) =>
                  setTopicForm({
                    ...topicForm,
                    learningOutcomes: e.target.value,
                  })
                }
                rows="3"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginBottom: "1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "8px",
                  background: getCardBackground(),
                  color: getTextColor(),
                }}
              />
              <textarea
                placeholder="Prerequisites (one per line)"
                value={topicForm.prerequisites}
                onChange={(e) =>
                  setTopicForm({ ...topicForm, prerequisites: e.target.value })
                }
                rows="2"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginBottom: "1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "8px",
                  background: getCardBackground(),
                  color: getTextColor(),
                }}
              />
              <input
                type="text"
                placeholder="Tags (comma separated)"
                value={topicForm.tags}
                onChange={(e) =>
                  setTopicForm({ ...topicForm, tags: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginBottom: "1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "8px",
                  background: getCardBackground(),
                  color: getTextColor(),
                }}
              />
              <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
                <button
                  onClick={() => setShowTopicModal(false)}
                  style={{
                    flex: 1,
                    padding: "0.8rem",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTopic}
                  style={{
                    flex: 1,
                    padding: "0.8rem",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default LearningRoadmapManager;
